import { autoinject, bindable, BindingEngine } from 'aurelia-framework';
import { NetsSalesDashboardService, NetsSalesHistoryFilter } from 'services/statistics-dashboard-service';
import { IndustryService } from 'services/industry-service';
import { ListSettingsService } from 'services/list-settings-service';
import { I18N } from 'aurelia-i18n';
import { AsyncErrorHandler } from 'lib/ui';
import { TableHeader } from 'components/table/table-content';
import { FilterManager } from 'lib/tables/FilterManager';
import { PubSub } from 'lib/event/PubSub';
import { NetsSalesHistoryDto } from 'models/statistics/nets';

@autoinject
export class NetsSalesHistory {
  private NETS_SALES_FILTERS_KEY = 'NETS_SALES_FILTERS_KEY';

  private tableData: Array<NetsSalesHistoryDto>;
  private totalItems;
  private ready: boolean = false;
  private searchInputHasFocus: boolean = false;
  private columns: any[] = [
    `${this.t.tr('net.customer')}`,
    `${this.t.tr('general.last')} ${this.t.tr('general.sales')} ${this.t.tr('general.date')}`,
    `${this.t.tr('net.averageIntervalBetweenSales')} (${this.t.tr('general.days')})`,
    `${this.t.tr('general.averageNetsInOrder')}`,
    `${this.t.tr('general.totalSales')}`,
    `${this.t.tr('general.numberOfOrders')}`,
  ];

  private params: any;
  @bindable private pageSize = 25;
  private context: string = '';
  @bindable private currentPage: number = 1;

  private tableViewType: number = 1;
  private tableViewVerticalLines: boolean = false;
  private tableMargin: number = 2;

  private filters: NetsSalesHistoryFilter = {
    searchText: null,
    orderBy: 'LastSaleDate',
    top: this.pageSize,
    skip: null,
    count: true,
    orderByDirection: 'DESC',
  };

  protected tableHeaders: TableHeader<NetsSalesHistoryDto>[] = [
    { key: 'CustomerName', label: `${this.t.tr('net.customer')}`, sortable: true },
    { key: 'LastSaleDate', label: `${this.t.tr('general.last')} ${this.t.tr('general.sales')} ${this.t.tr('general.date')}`, sortable: true },
    { key: 'IntervalBetweenSales', label: `${this.t.tr('net.averageIntervalBetweenSales')} (${this.t.tr('general.days')})`, sortable: true },
    { key: 'AverageNetsInOrder', label: `${this.t.tr('general.averageNetsInOrder')}`, sortable: true },
    { key: 'TotalSales', label: `${this.t.tr('general.totalSales')}`, sortable: true },
    { key: 'NumberOfOrders', label: `${this.t.tr('general.numberOfOrders')}`, sortable: true },
  ];

  constructor(
    private bindingEngine: BindingEngine,
    private netsSalesDashboardService: NetsSalesDashboardService,
    private listSettingsService: ListSettingsService,
    private industryService: IndustryService,
    private t: I18N,
    private filterManager: FilterManager,
    private pubsub: PubSub
  ) {
    pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
  }

  protected async activate(params) {
    this.params = params;
    await this.industryService.getAllCached();
  }

  onLocaleChanged() {
    this.columns = [
      `${this.t.tr('net.customer')}`,
      `${this.t.tr('general.last')} ${this.t.tr('general.sales')} ${this.t.tr('general.date')}`,
      `${this.t.tr('net.averageIntervalBetweenSales')} (${this.t.tr('general.days')})`,
      `${this.t.tr('general.averageNetsInOrder')}`,
      `${this.t.tr('general.totalSales')}`,
      `${this.t.tr('general.numberOfOrders')}`,
    ];
  }

  protected attached() {
    this.pubsub.sub('filter-manager:filter-changed', () => {
      void this.getNetsSalesHistory();
    });

    this.listSettingsService.getDefaultPageSize().then((defaultPageSize) => {
      this.pageSize = defaultPageSize;

      if (this.params?.currentPage > 1) {
        this.currentPage = this.params.currentPage;
      }

      if (this.params?.searchText) {
        this.filters.searchText = this.params.searchText;
      }

      this.bindingEngine.propertyObserver(this.filters, 'searchText').subscribe((newValue, oldValue) => {
        this.filterValueChanged('searchText', newValue, oldValue);
      });

      this.getNetsSalesHistory();
    });
  }

  protected setOrderByColumn(column: string) {
    this.filterManager.setOrderByColumn(column);
  }

  protected async currentPageChanged(newValue, oldValue) {
    if (parseInt(newValue, 10) === parseInt(oldValue, 10)) {
      return;
    }

    const stateObj = this.updateStateObject('currentPage', newValue);
    this.replaceState(stateObj);

    this.filters.skip = (newValue - 1) * this.filters.top;
    await this.getNetsSalesHistory();
  }

  private async filterValueChanged(key, newValue, oldValue) {
    if (newValue === oldValue) {
      return;
    }
    this.currentPage = 1;
    const stateObj = this.updateStateObject(key, newValue);
    this.replaceState(stateObj);
    await this.getNetsSalesHistory();
  }

  private updateStateObject(keyToUpdate, newValue) {
    const stateObj = {
      currentPage: this.currentPage,
      searchText: this.filters.searchText,
    };
    stateObj[keyToUpdate] = newValue;
    return stateObj;
  }

  private buildQueryString(obj) {
    const arr = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        // dont include keys where value is null
        if (obj[p]) {
          arr.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
        }
      }
    }
    return arr.join('&');
  }

  private replaceState(stateObj) {
    const baseUrl = '#statistics-dashboard';
    const queryString = this.buildQueryString(stateObj);
    history.replaceState(stateObj, 'statistics-dashboard', baseUrl + '?' + queryString);
  }

  @AsyncErrorHandler
  private async getNetsSalesHistory() {
    this.filters.top = this.pageSize;
    this.filters.skip = (this.currentPage - 1) * this.pageSize;

    const { data, headers } = await this.netsSalesDashboardService.getNetsSalesHistory(this.filters);

    if (data) {
      this.totalItems = headers.get('x-total-count');
      this.tableData = data;
      this.ready = true;
    }
  }

  private getFilterkey() {
    return this.NETS_SALES_FILTERS_KEY;
  }

  private saveFilters() {
    localStorage.setItem(this.getFilterkey(), JSON.stringify(this.filters));
  }

  private async sortTable(field) {
    if (this.filters.orderBy === field) {
      this.filters.orderByDirection = this.filters.orderByDirection === 'DESC' ? 'ASC' : 'DESC';
    } else {
      this.filters.orderBy = field;
      this.filters.orderByDirection = 'DESC';
    }

    this.saveFilters();
    await this.getNetsSalesHistory();
  }
}
